import React, {useEffect, useState} from 'react';
import {useFormContext} from "react-hook-form";
import SummaryItem from "./SummaryItem";
import {
    fa2, faCamera,
    faDoorClosed, faFrame,
    faGripLinesVertical, faKey,
    faPalette, faShield, faTools,
    faUpRightAndDownLeftFromCenter,
    faPoundSign
} from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import { useAppContext } from "../../context/AppContext";
import {
    ACCESSORIES_PRICES_TRADE_PENCE,
    EXTERNAL_HANDLE_BLACK_SURCHARGE_TRADE_PENCE,
} from "../../lib/constant";

function SummaryList({ doorPricePence = null, type = "trade", basketItem = null, scrollable = true, hidePrices = false }) {
    // When basketItem is provided (basket page), use its data directly.
    // Otherwise fall back to the react-hook-form context (configurator page).
    const formContext = useFormContext();
    const { csrfToken } = useAppContext();
    const [pricePence, setPricePence] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    const [priceError, setPriceError] = useState("");

    // If we have a basketItem, build values from it; otherwise watch the form
    const formValues = formContext ? formContext.watch([
        "height","width","aperture_height","aperture_width","external_door_style","external_door_colour","external_door_colour_type","external_handle_style","external_handle_length","external_handle_colour","glass_option"
        ,"house_number","house_number_style","spy_hole","cill","e_key_entry","escutcheon","top_frame_extender","side_frame_extender","hinge","opening_direction","internal_door_colour_type","internal_door_colour","oxide_frame_colour","panel","external_slab_colour_type","internal_slab_colour","internal_slab_colour_type","external_custom_ral_color","internal_custom_ral_color","internal_handle_style"
    ]) : [];

    // Resolve values: basketItem takes priority, then form context
    const bi = basketItem || {};
    const height = basketItem ? bi.height : (formValues[0] ?? "");
    const width = basketItem ? bi.width : (formValues[1] ?? "");
    const aperture_height = basketItem ? bi.aperture_height : (formValues[2] ?? "");
    const aperture_width = basketItem ? bi.aperture_width : (formValues[3] ?? "");
    const external_door_style = basketItem ? bi.external_door_style : (formValues[4] ?? "");
    const external_door_colour = basketItem ? bi.external_door_colour : (formValues[5] ?? "");
    const external_door_colour_type = basketItem ? bi.external_door_colour_type : (formValues[6] ?? "");
    const external_handle_style = basketItem ? bi.external_handle_style : (formValues[7] ?? "");
    const external_handle_length = basketItem ? bi.external_handle_length : (formValues[8] ?? "");
    const external_handle_colour = basketItem ? bi.external_handle_colour : (formValues[9] ?? "");
    const glass_option = basketItem ? bi.glass_option : (formValues[10] ?? "");
    const house_number = basketItem ? bi.house_number : (formValues[11] ?? "");
    const house_number_style = basketItem ? bi.house_number_style : (formValues[12] ?? "");
    const spy_hole = basketItem ? bi.spy_hole : (formValues[13] ?? "");
    const cill = basketItem ? bi.cill : (formValues[14] ?? "");
    const e_key_entry = basketItem ? bi.e_key_entry : (formValues[15] ?? "");
    const escutcheon = basketItem ? bi.escutcheon : (formValues[16] ?? "");
    const top_frame_extender = basketItem ? bi.top_frame_extender : (formValues[17] ?? "");
    const side_frame_extender = basketItem ? bi.side_frame_extender : (formValues[18] ?? "");
    const hinge = basketItem ? bi.hinge : (formValues[19] ?? "");
    const opening_direction = basketItem ? bi.opening_direction : (formValues[20] ?? "");
    const internal_door_colour_type = basketItem ? bi.internal_door_colour_type : (formValues[21] ?? "");
    const internal_door_colour = basketItem ? bi.internal_door_colour : (formValues[22] ?? "");
    const oxide_frame_colour = basketItem ? bi.oxide_frame_colour : (formValues[23] ?? "");
    const panel = basketItem ? bi.panel : (formValues[24] ?? "");
    const external_slab_colour_type = basketItem ? bi.external_slab_colour_type : (formValues[25] ?? "");
    const internal_slab_colour = basketItem ? bi.internal_slab_colour : (formValues[26] ?? "");
    const internal_slab_colour_type = basketItem ? bi.internal_slab_colour_type : (formValues[27] ?? "");
    const external_custom_ral_color = basketItem ? bi.external_custom_ral_color : (formValues[28] ?? "");
    const internal_custom_ral_color = basketItem ? bi.internal_custom_ral_color : (formValues[29] ?? "");
    const internal_handle_style = basketItem ? bi.internal_handle_style : (formValues[30] ?? "");

    const key = window.location.pathname.split('/')[1];

    const formatGBPFromPence = (amountPence) => {
        const amountNumber = Number(amountPence ?? 0);
        const safePence = Number.isFinite(amountNumber) ? Math.round(amountNumber) : 0;
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
        }).format(safePence / 100);
    };

    useEffect(() => {
        if (formContext) {
            console.log(formContext.getValues());
        }
    }, []);

    const resolvedExternalDoorColourType = external_custom_ral_color ? "non standard" : external_door_colour_type;
    const resolvedInternalDoorColourType = internal_custom_ral_color ? "non standard" : internal_door_colour_type;
    const resolvedExternalDoorColour = external_custom_ral_color || external_door_colour;
    const resolvedInternalDoorColour = internal_custom_ral_color || internal_door_colour;

    // Apply 40% markup for retail/customer configurators (type !== 'trade')
    const retailMultiplier = type !== 'trade' ? 1.4 : 1;
    const surchargePrice = (tradePence) => Math.round(tradePence * retailMultiplier);
    const fmt = (pence) => formatGBPFromPence(surchargePrice(pence));

    const surcharges = [];
    const eKeyEntryValue = String(e_key_entry ?? "").trim();
    if (eKeyEntryValue && eKeyEntryValue !== "none") {
        surcharges.push({ name: "E-Key entry", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.e_key_entry) });
    }

    // Non-standard colour surcharge: check frame AND panel colours
    const isNonStdExtSlab = String(external_slab_colour_type ?? "").trim() === "nonstandard";
    const isNonStdIntSlab = String(internal_slab_colour_type ?? "").trim() === "nonstandard";
    if (resolvedExternalDoorColourType === "non standard" || resolvedInternalDoorColourType === "non standard" || isNonStdExtSlab || isNonStdIntSlab) {
        surcharges.push({ name: "Non standard colour", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.non_standard) });
    }

    // Dual colour: when external frame colour differs from internal frame colour
    if (resolvedExternalDoorColour && resolvedInternalDoorColour && resolvedExternalDoorColour !== resolvedInternalDoorColour) {
        surcharges.push({ name: "Dual colour (Internal ≠ External)", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.dual_sided) });
    }

    // Black handle surcharge
    const extHandleColour = String(external_handle_colour ?? "").trim().toLowerCase();
    if (extHandleColour === "black") {
        surcharges.push({ name: "Black external handle", value: fmt(EXTERNAL_HANDLE_BLACK_SURCHARGE_TRADE_PENCE) });
    }

    const houseNumberValue = String(house_number ?? "").trim();
    if (houseNumberValue) {
        surcharges.push({ name: "House number", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.house_number) });
    }

    const spyHoleValue = String(spy_hole ?? "").trim();
    if (spyHoleValue && spyHoleValue !== "none") {
        surcharges.push({ name: "Spy hole", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.spy_hole) });
    }

    const sideExtenderValue = String(side_frame_extender ?? "").trim().toLowerCase();
    if (sideExtenderValue && sideExtenderValue !== "none" && sideExtenderValue.includes("35")) {
        surcharges.push({ name: "35mm side extender", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.frame_extender_side_35) });
    }

    const topExtenderValue = String(top_frame_extender ?? "").trim().toLowerCase();
    if (topExtenderValue && topExtenderValue !== "none" && topExtenderValue.includes("35")) {
        surcharges.push({ name: "35mm top extender", value: fmt(ACCESSORIES_PRICES_TRADE_PENCE.frame_extender_top_35) });
    }

    // Price calculation effect - only run if doorPricePence is not provided
    useEffect(() => {
        // If doorPricePence is provided, use that instead of making API call
        if (doorPricePence !== null) {
            setPricePence(doorPricePence);
            setPriceLoading(false);
            setPriceError("");
            return;
        }

        const heightNumber = Number(height);
        const widthNumber = Number(width);
        const apertureHeightNumber = Number(aperture_height);
        const apertureWidthNumber = Number(aperture_width);

        const hasRequiredNumbers =
            Number.isFinite(heightNumber) &&
            Number.isFinite(widthNumber) &&
            Number.isFinite(apertureHeightNumber) &&
            Number.isFinite(apertureWidthNumber);

        if (!hasRequiredNumbers || !external_door_style) {
            setPriceError("");
            setPricePence(null);
            return;
        }

        let active = true;
        const timeoutId = setTimeout(async () => {
            try {
                setPriceLoading(true);
                setPriceError("");
                const res = await axios.post("/basket/preview-price", {
                    height: heightNumber,
                    width: widthNumber,
                    aperture_height: apertureHeightNumber,
                    aperture_width: apertureWidthNumber,
                    external_door_style,
                    external_door_colour_type,
                    internal_door_colour_type,
                    external_custom_ral_color,
                    internal_custom_ral_color,
                    external_door_colour,
                    internal_door_colour,
                    external_handle_colour,
                    external_slab_colour_type,
                    internal_slab_colour_type,
                    e_key_entry,
                    house_number,
                    spy_hole,
                    side_frame_extender,
                    top_frame_extender,
                    type: type, // Use the actual type (trade/retail)
                    postcode: "", // No postcode needed for door price only
                    _token: csrfToken,
                    key,
                });

                if (!active) {
                    return;
                }

                const nextPence = Number(res?.data?.subtotal_amount);
                setPricePence(Number.isFinite(nextPence) ? nextPence : null);
                setPriceLoading(false);
            } catch (e) {
                if (!active) {
                    return;
                }
                setPriceLoading(false);
                setPricePence(null);
                setPriceError("Price unavailable");
            }
        }, 450);

        return () => {
            active = false;
            clearTimeout(timeoutId);
        };
    }, [
        doorPricePence,
        height,
        width,
        aperture_height,
        aperture_width,
        external_door_style,
        external_door_colour_type,
        internal_door_colour_type,
        external_custom_ral_color,
        internal_custom_ral_color,
        external_door_colour,
        internal_door_colour,
        external_handle_colour,
        external_slab_colour_type,
        internal_slab_colour_type,
        e_key_entry,
        house_number,
        spy_hole,
        side_frame_extender,
        top_frame_extender,
        type,
        csrfToken,
        key,
    ]);
    return (
        <div className={`flex flex-col summary-list ${scrollable ? 'h-full overflow-y-auto' : 'w-full'}`}>
            <SummaryItem icon={faUpRightAndDownLeftFromCenter} title="Aperture Dimension" items={[{name:"Width",value:`${aperture_width}mm`},{name:'Height',value:`${aperture_height}mm`}]}/>
            <SummaryItem icon={faUpRightAndDownLeftFromCenter} title="Door Dimension" items={[{name:"Width",value:`${width}mm`},{name:'Height',value:`${height}mm`}]}/>
            <SummaryItem icon={faUpRightAndDownLeftFromCenter} title="Opening Direction" items={[{value:opening_direction}]}/>
            <SummaryItem icon={faDoorClosed} title="Door Style" items={[{value:external_door_style}]}/>
            <SummaryItem
                icon={faPalette}
                title="External Door Colour"
                items={[{value:external_door_colour_type},{value:external_door_colour}]}
                subItems={panel && panel !== external_door_colour ? [{value:external_slab_colour_type},{value:panel}] : null}
                subTitle={panel && panel !== external_door_colour ? "External Panel Colour" : null}
            />
            {external_door_colour_type === "oxide" &&
              <SummaryItem icon={faPalette} title="Oxide Frame Colour" items={[{value:oxide_frame_colour}]}/>
            }
            <SummaryItem
                icon={faPalette}
                title="Internal Door Colour"
                items={[{value:internal_door_colour_type},{value:internal_door_colour}]}
                subItems={internal_slab_colour && internal_slab_colour !== internal_door_colour ? [{value:internal_slab_colour_type},{value:internal_slab_colour}] : null}
                subTitle={internal_slab_colour && internal_slab_colour !== internal_door_colour ? "Internal Panel Colour" : null}
            />
            {internal_door_colour_type === "oxide" &&
              <SummaryItem icon={faPalette} title="Oxide Frame Colour" items={[{value:oxide_frame_colour}]}/>
            }
            <SummaryItem icon={faGripLinesVertical} title="External Handle Style" items={[{value:external_handle_style},{value:`${external_handle_length}mm`}]}/>
            <SummaryItem icon={faFrame} title="Glass Option" items={[{value:glass_option}]}/>
            {/*<SummaryItem icon={faGripLinesVertical} title="Type And Configuration" items={[{value:glass_options}]}/>*/}
            <SummaryItem icon={fa2} title="House Number" items={[{value:house_number_style},{value:house_number}]}/>
            <SummaryItem icon={faFrame} title="Cill Options" items={[{value:cill}]}/>
            <SummaryItem icon={faCamera} title="Spy Hole" items={[{value:spy_hole}]}/>
            <SummaryItem icon={faKey} title="E-Key Entry" items={[{value:e_key_entry}]}/>
            <SummaryItem icon={faShield} title="Escutcheon" items={[{value:escutcheon}]}/>
            <SummaryItem icon={faFrame} title="Frame Extender" items={[{name:"Top",value:top_frame_extender},{name:"Side",value:side_frame_extender}]}/>
            <SummaryItem icon={faTools} title="Door Hinge" items={[{value:hinge}]}/>
            {!hidePrices && surcharges.length > 0 && (
                <SummaryItem icon={faPoundSign} title="Surcharges" items={surcharges} layout="rows" />
            )}

        </div>
    );
}

export default SummaryList;
